import React, { useState, useEffect } from 'react';
import settingsService from '../../../services/settingsService';
import breedService from '../../../services/breedService';
import pedigreeService from '../../../services/pedigreeService';
import expenseCategoryService from '../../../services/expenseCategoryService';
import expenseTypeService from '../../../services/expenseTypeService';

const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [breeds, setBreeds] = useState([]);
  const [newBreed, setNewBreed] = useState('');
  const [breedLoading, setBreedLoading] = useState(false);

  const [pedigrees, setPedigrees] = useState([]);
  const [newPedigree, setNewPedigree] = useState({ name: '', breed: '', notes: '' });
  const [pedigreeLoading, setPedigreeLoading] = useState(false);

  // ── Expense Categories ──
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);

  // ── Expense Types ──
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [newExpenseType, setNewExpenseType] = useState({ category: '', name: '', default_price: '', unit: '' });
  const [expenseTypeLoading, setExpenseTypeLoading] = useState(false);

  const [formData, setFormData] = useState({
    cow_milk_price: '',
    buffalo_milk_price: '',
  });

  useEffect(() => {
    fetchSettings();
    fetchBreeds();
    fetchPedigrees();
    fetchExpenseCategories();
    fetchExpenseTypes();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getSettings();
      setFormData({
        cow_milk_price: response.data.cow_milk_price || '',
        buffalo_milk_price: response.data.buffalo_milk_price || '',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      alert('❌ Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBreeds = async () => {
    try {
      const res = await breedService.getAllBreeds();
      setBreeds(res.data || []);
    } catch (error) {
      console.error('Error fetching breeds:', error);
    }
  };

  const handleAddBreed = async (e) => {
    e.preventDefault();
    if (!newBreed.trim()) return;
    try {
      setBreedLoading(true);
      await breedService.createBreed(newBreed.trim());
      setNewBreed('');
      fetchBreeds();
    } catch (error) {
      alert('❌ Failed to add breed');
    } finally {
      setBreedLoading(false);
    }
  };

  const handleDeleteBreed = async (id) => {
    if (!window.confirm('Delete this breed?')) return;
    try {
      await breedService.deleteBreed(id);
      fetchBreeds();
    } catch (error) {
      alert('❌ Failed to delete breed');
    }
  };

  const fetchPedigrees = async () => {
    try {
      const res = await pedigreeService.getAllPedigrees();
      setPedigrees(res.data || []);
    } catch (error) {
      console.error('Error fetching pedigrees:', error);
    }
  };

  const handleAddPedigree = async (e) => {
    e.preventDefault();
    if (!newPedigree.name.trim()) {
      alert('❌ Pedigree name is required');
      return;
    }
    try {
      setPedigreeLoading(true);
      await pedigreeService.createPedigree({
        name: newPedigree.name.trim(),
        breed: newPedigree.breed.trim() || null,
        notes: newPedigree.notes.trim() || null,
      });
      setNewPedigree({ name: '', breed: '', notes: '' });
      fetchPedigrees();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to add pedigree'));
    } finally {
      setPedigreeLoading(false);
    }
  };

  const handleDeletePedigree = async (id) => {
    if (!window.confirm('Delete this pedigree? Animals using it will have their pedigree cleared.')) return;
    try {
      await pedigreeService.deletePedigree(id);
      fetchPedigrees();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to delete pedigree'));
    }
  };

  // ── Expense Category handlers ──
  const fetchExpenseCategories = async () => {
    try {
      const res = await expenseCategoryService.getAll();
      setExpenseCategories(res.data || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    try {
      setCategoryLoading(true);
      await expenseCategoryService.create(newCategory.trim());
      setNewCategory('');
      fetchExpenseCategories();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to add category'));
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Expense types under it will remain but the category will no longer appear in dropdowns.')) return;
    try {
      await expenseCategoryService.delete(id);
      fetchExpenseCategories();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to delete category'));
    }
  };

  // ── Expense Type handlers ──
  const fetchExpenseTypes = async () => {
    try {
      const res = await expenseTypeService.getAll();
      setExpenseTypes(res.data || []);
    } catch (error) {
      console.error('Error fetching expense types:', error);
    }
  };

  const handleAddExpenseType = async (e) => {
    e.preventDefault();
    if (!newExpenseType.category) {
      alert('❌ Please select a category');
      return;
    }
    if (!newExpenseType.name.trim()) {
      alert('❌ Expense type name is required');
      return;
    }
    try {
      setExpenseTypeLoading(true);
      await expenseTypeService.create({
        category: newExpenseType.category,
        name: newExpenseType.name.trim(),
        default_price: parseFloat(newExpenseType.default_price) || 0,
        unit: newExpenseType.unit.trim() || null,
      });
      setNewExpenseType({ category: '', name: '', default_price: '', unit: '' });
      fetchExpenseTypes();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to add expense type'));
    } finally {
      setExpenseTypeLoading(false);
    }
  };

  const handleDeleteExpenseType = async (id) => {
    if (!window.confirm('Delete this expense type?')) return;
    try {
      await expenseTypeService.delete(id);
      fetchExpenseTypes();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to delete expense type'));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await settingsService.updateSettings(formData);
      alert('✅ Settings saved successfully!');
    } catch (error) {
      alert('❌ Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h2 style={{ color: '#666' }}>Loading settings...</h2>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>
          ⚙️ Settings
        </h2>
        <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
          Configure farm settings and milk prices
        </p>
      </div>

      {/* Milk Prices Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>🥛 Milk Prices</h3>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '24px' }}>
          Set the price per liter for cow and buffalo milk. These prices will be used for all milk sale calculations.
        </p>

        <form onSubmit={handleSave}>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                🐄 Cow Milk Price (PKR/Liter)
              </label>
              <div style={styles.inputWrapper}>
                <span style={styles.prefix}>Rs</span>
                <input
                  type="number"
                  step="1"
                  value={formData.cow_milk_price}
                  onChange={(e) => setFormData({ ...formData, cow_milk_price: e.target.value })}
                  style={styles.input}
                  placeholder="e.g. 100"
                  required
                />
                <span style={styles.suffix}>/L</span>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                🐃 Buffalo Milk Price (PKR/Liter)
              </label>
              <div style={styles.inputWrapper}>
                <span style={styles.prefix}>Rs</span>
                <input
                  type="number"
                  step="1"
                  value={formData.buffalo_milk_price}
                  onChange={(e) => setFormData({ ...formData, buffalo_milk_price: e.target.value })}
                  style={styles.input}
                  placeholder="e.g. 120"
                  required
                />
                <span style={styles.suffix}>/L</span>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div style={styles.preview}>
            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>
              💰 Price Preview
            </h4>
            <div style={styles.previewGrid}>
              <div style={styles.previewItem}>
                <span style={{ color: '#7f8c8d' }}>10L Cow Milk:</span>
                <strong>Rs {(parseFloat(formData.cow_milk_price) * 10 || 0).toLocaleString()}</strong>
              </div>
              <div style={styles.previewItem}>
                <span style={{ color: '#7f8c8d' }}>10L Buffalo Milk:</span>
                <strong>Rs {(parseFloat(formData.buffalo_milk_price) * 10 || 0).toLocaleString()}</strong>
              </div>
            </div>
          </div>

          <button type="submit" style={styles.saveButton} disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Settings'}
          </button>
        </form>
      </div>

      {/* Breeds Card */}
      <div style={{ ...styles.card, marginTop: '24px' }}>
        <h3 style={styles.cardTitle}>🐄 Manage Breeds</h3>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '24px' }}>
          Add or remove animal breeds. These will appear in the breed dropdown when adding animals.
        </p>
        <form onSubmit={handleAddBreed} style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <input
            type="text"
            value={newBreed}
            onChange={(e) => setNewBreed(e.target.value)}
            placeholder="Enter breed name (e.g. Sahiwal)"
            style={{ flex: 1, padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
          <button type="submit" disabled={breedLoading} style={{ padding: '10px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
            ➕ Add Breed
          </button>
        </form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {breeds.length === 0 ? (
            <p style={{ color: '#95a5a6', fontSize: '14px' }}>No breeds added yet.</p>
          ) : breeds.map(breed => (
            <div key={breed.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f0f4ff', border: '2px solid #d0d9f5', borderRadius: '20px', padding: '6px 14px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{breed.name}</span>
              <button onClick={() => handleDeleteBreed(breed.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: '700', fontSize: '16px', padding: '0', lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Pedigrees Card */}
      <div style={{ ...styles.card, marginTop: '24px' }}>
        <h3 style={styles.cardTitle}>🐂 Manage Bull Pedigrees</h3>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '24px' }}>
          Add or remove bull pedigrees. These will appear in the pedigree dropdown when marking an animal as pregnant.
        </p>
        <form onSubmit={handleAddPedigree} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '12px', marginBottom: '20px' }}>
          <input
            type="text"
            value={newPedigree.name}
            onChange={(e) => setNewPedigree({ ...newPedigree, name: e.target.value })}
            placeholder="Bull name *"
            style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
          <input
            type="text"
            value={newPedigree.breed}
            onChange={(e) => setNewPedigree({ ...newPedigree, breed: e.target.value })}
            placeholder="Breed (optional)"
            style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
          <input
            type="text"
            value={newPedigree.notes}
            onChange={(e) => setNewPedigree({ ...newPedigree, notes: e.target.value })}
            placeholder="Notes (optional)"
            style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
          <button type="submit" disabled={pedigreeLoading} style={{ padding: '10px 24px', background: '#9b59b6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
            ➕ Add
          </button>
        </form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {pedigrees.length === 0 ? (
            <p style={{ color: '#95a5a6', fontSize: '14px' }}>No pedigrees added yet.</p>
          ) : pedigrees.map(p => (
            <div
              key={p.id}
              title={p.notes || ''}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f5edff', border: '2px solid #d9c3f5', borderRadius: '20px', padding: '6px 14px' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{p.name}</span>
                {p.breed && <span style={{ fontSize: '11px', color: '#7f8c8d' }}>{p.breed}</span>}
              </div>
              <button
                onClick={() => handleDeletePedigree(p.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: '700', fontSize: '16px', padding: '0', lineHeight: 1 }}
              >×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Expense Categories Card */}
      <div style={{ ...styles.card, marginTop: '24px' }}>
        <h3 style={styles.cardTitle}>📂 Manage Expense Categories</h3>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '24px' }}>
          Add or remove expense categories. These appear in the category dropdown when recording an expenditure.
        </p>
        <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="Enter category name (e.g. Feed)"
            style={{ flex: 1, padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
          <button type="submit" disabled={categoryLoading} style={{ padding: '10px 24px', background: '#16a085', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
            ➕ Add Category
          </button>
        </form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {expenseCategories.length === 0 ? (
            <p style={{ color: '#95a5a6', fontSize: '14px' }}>No categories added yet.</p>
          ) : expenseCategories.map(cat => (
            <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#e8f8f5', border: '2px solid #a3e4d7', borderRadius: '20px', padding: '6px 14px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{cat.name}</span>
              <button onClick={() => handleDeleteCategory(cat.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: '700', fontSize: '16px', padding: '0', lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Expense Types Card */}
      <div style={{ ...styles.card, marginTop: '24px' }}>
        <h3 style={styles.cardTitle}>💸 Manage Expense Types</h3>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '24px' }}>
          Add expense types with a default price. When an operator selects a type in the expenditure form, the price auto-fills (and can still be edited).
        </p>
        <form onSubmit={handleAddExpenseType} style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>Category *</label>
            <select
              value={newExpenseType.category}
              onChange={(e) => setNewExpenseType({ ...newExpenseType, category: e.target.value })}
              style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
            >
              <option value="">Select category</option>
              {expenseCategories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>Type Name *</label>
            <input
              type="text"
              value={newExpenseType.name}
              onChange={(e) => setNewExpenseType({ ...newExpenseType, name: e.target.value })}
              placeholder="e.g. Wanda Bag 50kg"
              style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>Price (Rs)</label>
            <input
              type="number"
              step="0.01"
              value={newExpenseType.default_price}
              onChange={(e) => setNewExpenseType({ ...newExpenseType, default_price: e.target.value })}
              placeholder="0"
              style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>Unit</label>
            <input
              type="text"
              value={newExpenseType.unit}
              onChange={(e) => setNewExpenseType({ ...newExpenseType, unit: e.target.value })}
              placeholder="bag"
              style={{ padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
            />
          </div>
          </div>
          <button type="submit" disabled={expenseTypeLoading} style={{ marginTop: '12px', padding: '10px 24px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
            ➕ Add
          </button>
        </form>

        {expenseCategories.length === 0 && (
          <p style={{ color: '#e67e22', fontSize: '13px', marginBottom: '16px' }}>
            ⚠️ Add at least one category above before adding expense types.
          </p>
        )}

        {/* Types grouped by category */}
        {expenseTypes.length === 0 ? (
          <p style={{ color: '#95a5a6', fontSize: '14px' }}>No expense types added yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[...new Set(expenseTypes.map(t => t.category))].map(category => (
              <div key={category}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#e67e22', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {category}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {expenseTypes.filter(t => t.category === category).map(type => (
                    <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fef5e7', border: '2px solid #fad7a0', borderRadius: '10px', padding: '8px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{type.name}</span>
                        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          Rs {parseFloat(type.default_price).toLocaleString()}{type.unit ? ` / ${type.unit}` : ''}
                        </span>
                      </div>
                      <button onClick={() => handleDeleteExpenseType(type.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: '700', fontSize: '16px', padding: '0', lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

const styles = {
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '28px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    maxWidth: '700px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: '8px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2c3e50',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  prefix: {
    padding: '10px 12px',
    background: '#f8f9fa',
    color: '#7f8c8d',
    fontSize: '14px',
    fontWeight: '600',
    borderRight: '2px solid #e0e0e0',
  },
  suffix: {
    padding: '10px 12px',
    background: '#f8f9fa',
    color: '#7f8c8d',
    fontSize: '14px',
    fontWeight: '600',
    borderLeft: '2px solid #e0e0e0',
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    outline: 'none',
    fontSize: '16px',
    fontWeight: '600',
    color: '#2c3e50',
    textAlign: 'center',
  },
  preview: {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  previewGrid: {
    display: 'flex',
    gap: '32px',
  },
  previewItem: {
    display: 'flex',
    gap: '8px',
    fontSize: '14px',
  },
  saveButton: {
    padding: '12px 32px',
    background: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
  },
};

export default SettingsTab;